import React, { type FunctionComponent, useState } from 'react'
import { Dialog } from '../../../common/Dialog'
import { API_HOST, authenticatedFetchRaw } from '../../../../lib'
import { logError } from '@tomra/datadog-browser-logging'
import { Button } from '@tomra/design-system-react/button'

type Props = {
  userInfo: UserInfoType
  onClose: (scheduledDelete: string | null) => void
}

export const DeleteUserDialog: FunctionComponent<Props> = ({ userInfo, onClose }) => {
  const [requestStatus, setRequestStatus] = useState<RequestStatusType | 'scheduled' | 'success'>('idle')
  const [deletionDate, setDeletionDate] = useState<string | null>(null)

  function cancel() {
    onClose(deletionDate)
  }

  async function deleteUser() {
    try {
      setRequestStatus('pending')

      const response = await authenticatedFetchRaw(`${API_HOST}/mytomra/v1.0/hotlinetool/delete-user`, {
        method: 'POST',
        body: JSON.stringify({
          id: userInfo.id,
          appContext: userInfo.appContext
        })
      }).run()

      if (response.status === 204) {
        setDeletionDate(new Date().toISOString())
        setRequestStatus('success')
        return
      }

      const { data: scheduled } = await response.json()
      setDeletionDate(scheduled)
      setRequestStatus('scheduled')
    } catch (error) {
      logError(new Error('Failed to delete user'), error as Error)
      setRequestStatus('failure')
    }
  }

  return (
    <Dialog ariaLabel="Delete user" onClose={cancel}>
      <div className="max-w-screen-sm min-w-screen-sm rounded-2xl bg-white shadow-md dark:bg-gray-900 p-6 grid gap-4">
        {requestStatus === 'pending' ? (
          <div>Deleting user...</div>
        ) : requestStatus === 'scheduled' && deletionDate ? (
          <>
            <h1 className="text-lg">{userInfo.email} was scheduled for delete</h1>

            <p>
              The user will be deleted{' '}
              {new Date(deletionDate).toLocaleString('default', {
                month: 'long',
                day: 'numeric',
                year: 'numeric'
              })}
              . Within this period the user will be able to login and cancel the deletion.
            </p>
            <div className="flex space-x-4 justify-end">
              <Button onClick={cancel}>Close</Button>
            </div>
          </>
        ) : requestStatus === 'success' ? (
          <>
            <h1 className="text-lg">User deleted successfully</h1>
            <p>The user had no pending payouts and was deleted.</p>
            <div className="flex space-x-4 justify-end">
              <Button onClick={cancel}>Close</Button>
            </div>
          </>
        ) : (
          <>
            <h1 className="text-lg">Delete user {userInfo.email}?</h1>
            <p>
              If the user has no pending payouts it will be delete immediately, otherwise the user will be scheduled for
              deletion in 30 days.
            </p>

            {requestStatus === 'failure' && (
              <div className="rounded-lg border-l-[3px] border-rose-500 bg-white p-4 text-left shadow-md dark:bg-gray-900">
                Failed to delete user
              </div>
            )}

            <div className="flex space-x-4 justify-end">
              <Button onClick={cancel}>Cancel</Button>
              <Button variant="destructive" onClick={deleteUser}>
                Delete
              </Button>
            </div>
          </>
        )}
      </div>
    </Dialog>
  )
}
